import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Loader2, Search, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { apiErrorMessage } from "../../lib/inventoryApi";

// PemilihCari — kotak pilih untuk data BESAR (customer, bahan, vendor, karyawan,
// penerimaan barang, WO). Pola daftar seragam 27 Sep 2026 (`docs/POLA_DAFTAR.md`).
//
// ⛔ KENAPA BUKAN <Select> BERISI SEMUA BARIS. Dropdown yang memuat daftar lewat
//    satu panggilan tanpa parameter berhenti di batas bawaan endpoint (50 customer)
//    TANPA TANDA: customer ke-51 tidak pernah bisa dipilih, dan staf menyimpulkan ia
//    belum terdaftar lalu membuat data kembar. Di sini server yang mencari: ketik
//    minimal `minHuruf` huruf → satu permintaan (jeda 250 ms) → paling banyak
//    `batas` hasil. Nilai terpilih SELALU tampil walau tidak ada di hasil cari.
//
// Props:
//   value        objek terpilih (atau null). Boleh juga id (string) bila `ambilSatu` diberi.
//   onChange     (objek|null) => void
//   cari         async (kata) => objek[]   — WAJIB; panggil server dengan `q` + batas
//   ambilSatu    async (id) => objek       — opsional, untuk `value` berupa id
//   label        (objek) => teks utama
//   keterangan   (objek) => teks kecil di bawahnya (opsional)
//   tampilAwal   true = tampilkan hasil tanpa mengetik (untuk daftar pendek/terbaru)
//   bolehKosong  true = ada tombol untuk mengosongkan pilihan
export default function PemilihCari({
  value, onChange, cari, ambilSatu,
  label = (x) => x?.name ?? x?.id ?? "",
  keterangan = null,
  placeholder = "Pilih…",
  placeholderCari = "Ketik untuk mencari…",
  minHuruf = 2, batas = 20, tampilAwal = false, bolehKosong = false,
  disabled = false, className = "", testId = "pemilih-cari",
}) {
  const [buka, setBuka] = useState(false);
  const [kata, setKata] = useState("");
  const [hasil, setHasil] = useState([]);
  const [memuat, setMemuat] = useState(false);
  const [galat, setGalat] = useState(false);
  const [terpilih, setTerpilih] = useState(typeof value === "object" ? value : null);
  const nomorPanggil = useRef(0);
  // Fungsi dari pemanggil biasanya ditulis langsung (identitas baru tiap render) —
  // menaruhnya di dependensi efek akan memicu pencarian ulang TANPA HENTI.
  const cariRef = useRef(cari);
  cariRef.current = cari;
  const ambilSatuRef = useRef(ambilSatu);
  ambilSatuRef.current = ambilSatu;

  // `value` berupa objek → langsung dipakai; berupa id → dibaca sekali lewat `ambilSatu`.
  useEffect(() => {
    if (value && typeof value === "object") { setTerpilih(value); return undefined; }
    if (!value) { setTerpilih(null); return undefined; }
    if (terpilih?.id === value || !ambilSatuRef.current) return undefined;
    let batal = false;
    ambilSatuRef.current(value)
      .then((o) => { if (!batal) setTerpilih(o || null); })
      .catch((e) => { if (!batal) toast.error(apiErrorMessage(e, "Pilihan yang tersimpan gagal dibaca.")); });
    return () => { batal = true; };
  }, [value, terpilih?.id]);

  const kataBersih = kata.trim();
  const cukup = kataBersih.length >= minHuruf || (tampilAwal && kataBersih.length === 0);

  useEffect(() => {
    if (!buka) return undefined;
    if (!cukup) { setHasil([]); setMemuat(false); return undefined; }
    const t = setTimeout(async () => {
      // Jawaban yang tiba tak berurutan DIBUANG — yang berlaku hanya panggilan terakhir.
      nomorPanggil.current += 1;
      const nomor = nomorPanggil.current;
      setMemuat(true); setGalat(false);
      try {
        const r = await cariRef.current(kataBersih);
        if (nomor === nomorPanggil.current) setHasil((Array.isArray(r) ? r : []).slice(0, batas));
      } catch {
        if (nomor === nomorPanggil.current) { setHasil([]); setGalat(true); }
      } finally {
        if (nomor === nomorPanggil.current) setMemuat(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [buka, kataBersih, cukup, batas]);

  const pilih = (o) => {
    setTerpilih(o);
    onChange?.(o);
    setBuka(false);
    setKata("");
  };

  let isiKosong = null;
  if (!memuat && !cukup) isiKosong = `Ketik minimal ${minHuruf} huruf untuk mencari.`;
  else if (!memuat && galat) isiKosong = "Pencarian gagal. Coba lagi sebentar lagi.";
  else if (!memuat && hasil.length === 0) isiKosong = "Tidak ada yang cocok.";

  return (
    <Popover open={buka} onOpenChange={setBuka}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={buka}
          disabled={disabled}
          data-testid={testId}
          className={`w-full justify-between font-normal ${className}`}
        >
          <span className={`truncate ${terpilih ? "" : "text-muted-foreground"}`} data-testid={`${testId}-nilai`}>
            {terpilih ? label(terpilih) : placeholder}
          </span>
          <ChevronDown size={16} className="opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[300px]" align="start">
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              data-testid={`${testId}-input`}
              autoFocus
              className="pl-8"
              placeholder={placeholderCari}
              value={kata}
              onChange={(e) => setKata(e.target.value)}
              aria-label="Kata pencarian"
            />
          </div>
        </div>
        <div className="max-h-72 overflow-auto" role="listbox">
          {memuat && (
            <div className="p-3 text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Mencari…
            </div>
          )}
          {isiKosong && <div className="p-3 text-xs text-muted-foreground" data-testid={`${testId}-kosong`}>{isiKosong}</div>}
          {!memuat && hasil.map((o) => (
            <button
              type="button"
              key={o.id}
              role="option"
              aria-selected={terpilih?.id === o.id}
              data-testid={`${testId}-option-${o.id}`}
              onClick={() => pilih(o)}
              className={`w-full text-left px-3 py-2 hover:bg-muted/50 flex flex-col ${terpilih?.id === o.id ? "bg-muted/40" : ""}`}
            >
              <span className="text-sm font-medium">{label(o)}</span>
              {keterangan && <span className="text-xs text-muted-foreground">{keterangan(o)}</span>}
            </button>
          ))}
          {!memuat && hasil.length >= batas && (
            <div className="px-3 py-2 text-[11px] text-muted-foreground border-t border-border">
              Menampilkan {batas} teratas — ketik lebih lengkap untuk mempersempit.
            </div>
          )}
        </div>
        {bolehKosong && terpilih && (
          <div className="border-t border-border p-1">
            <button type="button" className="w-full text-left px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted/50 flex items-center gap-1"
                    onClick={() => pilih(null)} data-testid={`${testId}-kosongkan`}>
              <X size={12} /> Kosongkan pilihan
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
